import { useLocation, Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { useTheme } from '../hooks/useTheme'
import {
  HomeIcon,
  HeartIcon,
  BellIcon,
  ComputerDesktopIcon,
  ChartBarIcon,
  BanknotesIcon,
  LightBulbIcon,
  ConnectIcon,
  CodeBracketIcon,
  CogIcon,
  SunIcon,
  MoonIcon,
  MonitorIcon,
  ArrowRightOnRectangleIcon,
  ArrowLeftOnRectangleIcon,
} from './icons'

// Runtime config for hiding API docs (single-tenant Okta deployments)
const runtimeConfig = (window as { __RUNTIME_CONFIG__?: Record<string, unknown> }).__RUNTIME_CONFIG__ || {}
const hideApiDocs = runtimeConfig.HIDE_API_DOCS === true

type NavItem = { path: string; label: string; icon: React.FC }

const navSections: { label: string; items: NavItem[] }[] = [
  {
    label: '',
    items: [
      { path: '/', label: 'Overview', icon: HomeIcon },
    ],
  },
  {
    label: 'Monitor',
    items: [
      { path: '/health', label: 'Device Health', icon: HeartIcon },
      { path: '/events', label: 'Events', icon: BellIcon },
      { path: '/computers', label: 'Computers', icon: ComputerDesktopIcon },
    ],
  },
  {
    label: 'Analyse',
    items: [
      { path: '/trendlines', label: 'Trendlines', icon: ChartBarIcon },
      { path: '/tco', label: 'Cost Optimization', icon: BanknotesIcon },
      { path: '/insights', label: 'Insights', icon: LightBulbIcon },
    ],
  },
  {
    label: 'Configure',
    items: [
      { path: '/integrations', label: 'Integrations', icon: ConnectIcon },
      { path: '/api', label: 'API', icon: CodeBracketIcon },
      { path: '/settings', label: 'Settings', icon: CogIcon },
    ],
  },
]

interface SidebarProps {
  isOpen?: boolean
}

export default function Sidebar({ isOpen = false }: SidebarProps) {
  const location = useLocation()
  const { theme, setTheme } = useTheme()
  const {
    isLoading,
    isAuthenticated,
    isEnabled,
    user,
    loginWithRedirect,
    logout,
  } = useAuth()

  const getThemeIcon = () => {
    if (theme === 'light') return <SunIcon />
    if (theme === 'dark') return <MoonIcon />
    return <MonitorIcon />
  }

  const getThemeLabel = () => {
    if (theme === 'light') return 'Light Mode'
    if (theme === 'dark') return 'Dark Mode'
    // Show effective theme when in auto mode
    const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches
    return prefersDark ? 'Auto (Dark)' : 'Auto (Light)'
  }

  const cycleTheme = () => {
    if (theme === 'light') setTheme('dark')
    else if (theme === 'dark') setTheme('system')
    else setTheme('light')
  }

  return (
    <aside className={`sidebar ${isOpen ? 'open' : ''}`}>
      <div className="sidebar-header">
        <img src="/jamf-lens-icon.svg" alt="Jamf Lens" className="sidebar-logo" />
        <h2>Jamf Lens</h2>
        <span className="alpha-badge">ALPHA</span>
      </div>
      <nav className="sidebar-nav">
        {navSections.map((section, idx) => {
          // Filter out /api page when HIDE_API_DOCS is set (single-tenant Okta deployments)
          const items = hideApiDocs
            ? section.items.filter(item => item.path !== '/api')
            : section.items

          // Skip empty sections after filtering
          if (items.length === 0) return null

          return (
            <div key={section.label || `section-${idx}`}>
              {section.label && <div className="sidebar-section-label">{section.label}</div>}
              {items.map((item) => {
                const Icon = item.icon
                const isActive = location.pathname === item.path
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`nav-item ${isActive ? 'active' : ''}`}
                  >
                    <Icon />
                    {item.label}
                  </Link>
                )
              })}
            </div>
          )
        })}
      </nav>
      <div className="sidebar-footer">
        {/* User Profile / Auth - only show if an auth provider is enabled */}
        {isEnabled && (
          <div className="sidebar-auth">
            {isLoading ? (
              <div className="auth-loading">Loading...</div>
            ) : isAuthenticated && user ? (
              <div className="auth-user">
                <div className="user-info">
                  {user.picture && (
                    <img src={user.picture} alt={user.name || 'User'} className="user-avatar" />
                  )}
                  <div className="user-details">
                    <span className="user-name">{user.name || user.email}</span>
                    {user.name && user.email && (
                      <span className="user-email">{user.email}</span>
                    )}
                  </div>
                </div>
                <button
                  className="auth-button logout"
                  onClick={logout}
                  title="Logout"
                >
                  <ArrowLeftOnRectangleIcon />
                  <span>Logout</span>
                </button>
              </div>
            ) : (
              <button
                className="auth-button login"
                onClick={() => loginWithRedirect()}
              >
                <ArrowRightOnRectangleIcon />
                <span>Login</span>
              </button>
            )}
          </div>
        )}

        <button
          className="theme-toggle"
          onClick={cycleTheme}
          title={`Theme: ${getThemeLabel()} (click to change)`}
        >
          {getThemeIcon()}
          <span>{getThemeLabel()}</span>
        </button>
      </div>
    </aside>
  )
}
